import { agreementSigned, agreementTextVersion, agreementVersionLabel, feeRecorded } from '../domain/agreement';
import { AGREEMENT_NEW_BUILD_DECLARATION_EN, AGREEMENT_NEW_BUILD_DECLARATION_ZH, AGREEMENT_NEW_BUILD_REPORT_TITLE_EN, AGREEMENT_NEW_BUILD_REPORT_TITLE_ZH } from '../domain/agreementText';
import { inspectionServiceOf, type InspectionService } from '../domain/inspectionService';
import type { V2Finding } from '../domain/finding';
import { clientNameOf, coverMediaKey, progressFor, type ProgressSummary, type UnableReason, type V2Area, type V2ChecklistItem } from '../domain/inspection';
import { isRoofObservationMethod, renderLimitationTemplate, resolveLimitationTemplate, type LimitationScope, type RoofObservationMethod } from '../domain/authority/ny/limitationTemplates';
import { variantLocale, type ReportOutputLanguage, type ReportVariant } from '../domain/report';
import { translate, translatePlural, type MessageKey, type PluralBase } from '../i18n/i18n';
import { areaKindLabel, basementFinishLabel, basementTypeLabel, descriptorChoiceLabel, descriptorLabel, descriptorValueLabel, floorLabelFor, priorityLabel, propertyTypeLabel, severityLabel, tradeLabel, unableReasonLabel, areaDisplayName, itemTitleFor } from '../i18n/labels';
import { DEFECT_SEVERITY_ORDER, findingSeverity, rowSeverity, type DefectSeverity } from '../domain/severity';
import { descriptorsForTemplate, itemDescriptorAnswer } from '../domain/descriptors';
import { templateRowScope } from '../domain/checklistTemplates';
import { wordingRemindersFor, type WordingReminder } from '../domain/wordingGuard';
import { reportBoundInspectorText, type WordingScanDeps } from './boundText';
import { getMedia } from '../persistence/v2Database';
import { resizeImage } from '../native/imageTools';
import type { PhotoAnnotation } from '../domain/annotation';
import { markedPhotoDataUrl } from './markPhoto';
import type { EvidenceState } from '../state/evidenceStore';
import type { FindingState } from '../state/findingStore';
import type { InspectionState } from '../state/inspectionStore';
import { agreementScopeBodyWithServicesFor, agreementTitleFor, scopeTextFor } from './agreementHtml';

// Report assembly — a pure derivation of the inspector-settled state into the report structure
// (Final Authority §20.1 order): Property Information · Client Information · Inspection Date ·
// Inspector · Summary · System / Area Sections (descriptive info → OBSERVED COMPONENTS → findings;
// the Owner-approved §20.1 deviation of 2026-09-06, NY F1 Round 2; a NOT PRESENT block only where an
// area has N/A or not-installed rows, D-17 ②) · Unable to Inspect ·
// Inspector Notes · agreement terms. Report information stays in its three layers (OC §8.6): the
// app's internal states (AI, pending, drafts) never appear. Every string comes from the dictionary
// of the REPORT locale (never the App Language). The Major Findings inclusion rule (D-17 ①) was decided by
// the Owner on 2026-10-19: every defect row and every defect / safety other finding, by tier — the
// Summary of Deficiencies (`defectSummary`), printed after the Summary and before the systems summary.
import { REVIEW_AREA_KIND, rowContextFor, rowSentenceFor, systemSummaries, type SystemSummary, type WordingFacts } from './rowWording';
import { defectFactsOfItem } from './defectFacts';
import { observationTextsOfItem, rowReferralTrade, type AiState } from '../state/aiStore';

export type PhotoLoader = (evidenceId: string) => Promise<string | null>;

export interface ReportKeyValue {
  key: string;
  value: string;
}

export interface ReportPhoto {
  evidenceId: string;
  caption: string;
  dataUrl: string | null;
  figure: string;
}

export interface ReportFinding {
  id: string;
  title: string;
  itemTitle: string | null;
  observation: string;
  recommendation: string;
  trade: string | null;
  priority: string;
  // 2026-10-19 (Owner item 16): the defect tier and its printed name. null = not a defect (an
  // informational note, a voluntary observation).
  severity: DefectSeverity | null;
  severityLabel: string;
  photos: ReportPhoto[];
  // The photos of the entry the report leaves out, counted ("3 more photos of this item are not included
  // in this report."). '' when none.
  morePhotosNote: string;
}

// Owner item 19: at most four photos per entry. 2026-10-28 (Owner, after the timing walk whose 85-photo
// PDF the phone's memory limit killed: 「报告可以精简一点，不需要每一张照片都放上去」): the photo appendix is
// gone — the photos beyond four are not printed, not even read. The inspector's own links come first, then
// the photos the AI placed, each in the order they were linked.
export const REPORT_PHOTOS_PER_ENTRY = 4;

// 2026-10-19 (Owner item 15 = D-17 ①): one line of the Summary of Deficiencies — every defect row and
// every defect / safety other finding, by tier (red first), with its area, the professional to call and
// its first photo. Routine-maintenance and advisory notes, voluntary observations and limitations are
// not in it.
export interface ReportSummaryLine {
  findingId: string;
  severity: DefectSeverity;
  severityLabel: string;
  title: string;
  areaName: string;
  trade: string | null;
  photo: string | null;
}

export interface ReportLimitation {
  itemTitle: string;
  reason: string;
  note: string;
  // NY F1 Round 3 (OD-5 = A): true for a limited PORTION of a row that keeps its observed result — the
  // row itself still prints under Observed Components (marked), never as a whole-row Unable and never
  // in NOT PRESENT. False for a whole-row Unable to Inspect.
  partial: boolean;
  // The portion in the inspector's words ('' = not named; the renderer prints "a part of this component").
  portion: string;
  // OD-7 = C: the frozen NY wording that states the reasons why when no note was typed ('' otherwise —
  // a typed note prints as the note, and for the other closed codes the label alone is the frozen R2 form).
  reasonText: string;
}

// NY F1 Round 2 (RP-14 / RP-R02): one line per row that was observed, with the inspector's result.
// Rows not observed for a limitation live in `limitations` (the renderer prints a "Not observed →"
// pointer for them); rows not present live in `notPresent`. Every settled row is in exactly one of
// the three, never in two and never in none. 'notRecorded' only survives in a preview (a completed
// inspection has no unrecorded row).
export type ObservedState = 'observed' | 'observedDefect' | 'notRecorded';

export interface ObservedRow {
  itemTitle: string;
  state: ObservedState;
  // The whole result as one string ("Observed · part not observed →").
  label: string;
  // The same result in the two pieces the renderer lays out (Owner device test 2026-09-21: one long
  // unbreakable label squeezed the component name to one character per line): the short state that sits
  // on the component's line, and the "part not observed →" pointer ('' when the row has none) that
  // prints on the line under it.
  stateLabel: string;
  partialLabel: string;
  // NY F1 Round 3 (OD-5 = A): the row carries limited portions — observed in part, printed as such.
  partial: boolean;
  // 2026-09-14: the sentence the report prints for this row (src/report/rowWording.ts); '' when not recorded.
  sentence: string;
  // 2026-09-13 (TW-2): a supplemental row prints as a voluntary observation beyond the New York minimum.
  supplemental: boolean;
}

// NY F1 Round 2 (RP-15 / D-17 ②): N/A and not-installed rows, printed with the frozen wording —
// never "could not be inspected", never "inspected, no defects".
export interface NotPresentRow {
  itemTitle: string;
  wording: string;
  note: string;
}

export interface ReportSection {
  areaId: string;
  title: string;
  subtitle: string;
  note: string;
  results: string;
  // NY F1 Round 5 (OD-13 / agreement v2 §3.5): what the inspector looked at at their own discretion,
  // outside the required scope. Printed in its own block, never among `findings`, never counted as
  // an observed component and never in the findings count of the summary.
  otherObservations: ReportFinding[];
  // 描述信息 — the mandatory descriptors of this area's observed rows (RP-06 … RP-13); "Not recorded"
  // when unanswered, never an invented value.
  descriptors: ReportKeyValue[];
  observed: ObservedRow[];
  notPresent: NotPresentRow[];
  findings: ReportFinding[];
  limitations: ReportLimitation[];
}

export interface ReportModel {
  locale: ReportOutputLanguage;
  variant: ReportVariant;
  // OD-9: which service this report documents. A New Build observation never calls itself a home
  // inspection report — not in the title, not in the file name, not in the scope statement.
  service: InspectionService;
  // Demo inspection: every page carries the DEMO watermark (Round 2 §AJ).
  demo: boolean;
  // Owner decision 2026-09-11 (D-1) names the product AI Home Inspector, but the deliverable must
  // never read as AI-authored (tests/state/reportFinalize: the client report contains no "AI"), so
  // the product name stays out of the report until the Owner rules on it (ledger 2026-09-11 Q-1).
  title: string;
  variantLabel: string | null;
  address: string;
  // Owner 2026-09-14: the inspection's cover photo, printed under the title. null = none was taken.
  coverPhoto: { dataUrl: string } | null;
  property: ReportKeyValue[];
  client: ReportKeyValue[];
  inspection: ReportKeyValue[];
  // The agreement is its own document (separate PDF); the report only states the facts about it.
  agreement: { document: string; fee: string; signed: string };
  summary: { areas: string; items: string; findings: string; limitations: string };
  // The Summary of Deficiencies (D-17 ①), in print order.
  defectSummary: ReportSummaryLine[];
  // Chinese self-copy only: "if it differs from the English version, the English version governs".
  governedNote: string | null;
  // 2026-10-19 (Owner item 18): company, contact, logo on the cover; the inspector photo beside the
  // inspector details; the running header / footer every PDF page carries. null = no branding set.
  branding: (ReportBrandingInput & { runningHeader: string; runningFooter: string }) | null;
  sections: ReportSection[];
  // 2026-09-14: the same check across locations, one paragraph per check type (src/report/rowWording.ts).
  systems: SystemSummary[];
  unable: Array<{ areaName: string } & ReportLimitation>;
  inspectorNotes: string;
  // "Not observed →" — the Observed Components pointer for a row that has a limitation entry.
  notObservedLabel: string;
  supplementalLabel: string;
  limitedScopeStatement: string;
  terms: string;
  generatedAt: string;
  labels: Record<
    'propertyInformation' | 'clientInformation' | 'inspectionDetails' | 'agreement' | 'agreementDocument' | 'fee' | 'agreementSigned' | 'agreementSeparate' | 'summary' | 'sections' | 'systemsSummary' | 'description' | 'observedComponents' | 'notPresent' | 'unable' | 'inspectorNotes' | 'terms' | 'scopeStatement' | 'observation' | 'recommendation' | 'responsibleTrade' | 'priority' | 'severity' | 'defectSummary' | 'defectSummaryNote' | 'defectSummaryEmpty' | 'unableSeeArea' | 'evidence' | 'areaNote' | 'noFindings' | 'reason' | 'note' | 'portion' | 'portionUnspecified' | 'otherObservations' | 'voluntaryTag' | 'generatedAt' | 'page' | 'demoBanner' | 'demoWatermark',
    string
  >;
}

export interface AssembleDeps {
  inspection: InspectionState;
  findings: FindingState;
  evidence: EvidenceState;
  loadPhoto?: PhotoLoader;
  // The cover photo bytes as a data URL; default = the media store. Tests pass their own.
  loadCover?: CoverLoader;
  generatedAt: number;
  // The row facts the report sentences read (CA-13): the AI observations filed on the rows and the
  // advanced review summaries. Absent = the inspector's note and result alone.
  ai?: AiState;
  advancedSummaries?: ReadonlyMap<string, string>;
  // The inspector's own names for the three tiers, per report language (Settings › Report). Absent or
  // '' = the standard names.
  severityLabels?: Partial<Record<ReportOutputLanguage, Partial<Record<DefectSeverity, string>>>>;
  // 2026-10-19 (Owner item 18): the inspector's branding as it prints — absent = none.
  branding?: ReportBrandingInput;
}

export interface ReportBrandingInput {
  companyName: string;
  contact: string;
  logo: string | null;
  inspectorPhoto: string | null;
}

function blobToDataUrl(blob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    } catch {
      resolve(null);
    }
  });
}

// Default photo loader: the app's media store. 2026-10-19 (Owner item 19): the report prints the photo
// itself, not the 360px thumbnail — the full image scaled to at most 1200px (sharp at the PDF's ~192 dpi
// two-across layout, bounded in memory), the thumbnail only when the full image cannot be read.
export const REPORT_PHOTO_MAX_EDGE = 1200;
export const mediaPhotoLoader: PhotoLoader = async (evidenceId) => {
  try {
    const full = await getMedia(evidenceId, 'full');
    if (full) {
      const resized = await resizeImage(full, { maxEdge: REPORT_PHOTO_MAX_EDGE, quality: 0.8 });
      return blobToDataUrl(resized ? resized.blob : full);
    }
    const thumbnail = await getMedia(evidenceId, 'thumbnail');
    return thumbnail ? blobToDataUrl(thumbnail) : null;
  } catch {
    return null;
  }
};

export type CoverLoader = (inspectionId: string) => Promise<string | null>;

// Default cover loader: the print-size bytes under coverMediaKey(id), the thumbnail as a fallback.
export const mediaCoverLoader: CoverLoader = async (inspectionId) => {
  try {
    const blob = (await getMedia(coverMediaKey(inspectionId), 'full')) ?? (await getMedia(coverMediaKey(inspectionId), 'thumbnail'));
    return blob ? blobToDataUrl(blob) : null;
  } catch {
    return null;
  }
};

async function coverFor(loader: CoverLoader, inspectionId: string): Promise<{ dataUrl: string } | null> {
  const dataUrl = await loader(inspectionId);
  return dataUrl ? { dataUrl } : null;
}

function formatDate(locale: ReportOutputLanguage, ts: number): string {
  try {
    return new Date(ts).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return new Date(ts).toISOString().slice(0, 10);
  }
}

function formatDateTime(locale: ReportOutputLanguage, ts: number): string {
  try {
    return new Date(ts).toLocaleString(locale, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return new Date(ts).toISOString();
  }
}

type PluralTranslator = (base: PluralBase, n: number, params?: Record<string, string | number>) => string;

function resultsLine(tp: PluralTranslator, p: ProgressSummary): string {
  return tp('report.itemResults', p.total, { pass: p.ok, defect: p.issues, unable: p.unable, na: p.notApplicable });
}

export async function assembleReport(inspectionId: string, variant: ReportVariant, deps: AssembleDeps): Promise<ReportModel> {
  const locale = variantLocale(variant);
  const t = (key: MessageKey, params?: Record<string, string | number>) => translate(locale, key, params);
  // Counts with their singular in the English copy (device walkthrough 2026-10-27 E4: never "1 findings").
  const tp: PluralTranslator = (base, n, params) => translatePlural(locale, base, n, params);
  const inspection = deps.inspection.inspections.find((i) => i.id === inspectionId);
  if (!inspection) throw new Error(`inspection ${inspectionId} not found`);
  const areas = deps.inspection.areas.filter((a) => a.inspectionId === inspectionId && a.enabled).sort((a, b) => a.sortOrder - b.sortOrder);
  const items = deps.inspection.items.filter((i) => i.inspectionId === inspectionId);
  const confirmed = deps.findings.findings.filter((f) => f.inspectionId === inspectionId && f.status === 'confirmed');
  const evidenceById = new Map(deps.evidence.evidence.map((e) => [e.id, e] as const));
  const loadPhoto = deps.loadPhoto ?? mediaPhotoLoader;
  const notRecorded = t('report.notRecorded');
  // Fact authority of a row's sentence (CA-13): inspector note › inspector result › AI / advanced
  // observations › other facts — rowSentenceFor applies that order; this only gathers the facts.
  const factsOf = (item: V2ChecklistItem): WordingFacts => ({
    photoNotes: item.evidenceIds.map((id) => evidenceById.get(id)?.caption.trim() ?? '').filter((c) => c.length > 0),
    aiObservations: deps.ai ? observationTextsOfItem(deps.ai, item.id, { rowStatus: item.status }) : [],
    advancedReview: deps.advancedSummaries?.get(item.id) ?? null,
    structuralConcern: item.structuralConcern
  });
  const tradeOf = (item: V2ChecklistItem): string | null => {
    const trade = rowReferralTrade(item);
    return trade ? tradeLabel(t, trade) : null;
  };
  // 2026-10-28 (iPhone run 10-26 #10): where the row is (a Safety Review row is at the house and points to the
  // section that recorded its check), what it may claim, and the AI's own facts for a Defect row nobody worded.
  const sentenceOf = (item: V2ChecklistItem, area: V2Area): string => {
    const context = rowContextFor(locale, area, item, areas, items);
    return rowSentenceFor(locale, item, context.areaName, factsOf(item), { tradeLabel: tradeOf(item), ...context.options, defectFacts: deps.ai ? defectFactsOfItem(deps.ai.observations, item.id, locale) : [] });
  };

  const p = inspection.property;
  const address = [p.address, p.city, [p.state, p.zip].filter((s) => s.length > 0).join(' ')].map((s) => s.trim()).filter((s) => s.length > 0).join(', ');
  const property: ReportKeyValue[] = [
    { key: t('report.address'), value: address.length > 0 ? address : notRecorded },
    { key: t('report.propertyType'), value: propertyTypeLabel(t, p.propertyType) },
    { key: t('report.yearBuilt'), value: p.yearBuilt ? String(p.yearBuilt) : notRecorded },
    {
      key: t('report.basement'),
      value: p.basementType ? `${basementTypeLabel(t, p.basementType)}${p.basementFinish ? ` · ${basementFinishLabel(t, p.basementFinish)}` : ''}` : notRecorded
    },
    // NY F1 Round 2 (CK-01 · 197-5.8(d) · 197-5.10(a)(1)): the property facts print here, so they
    // reach the report whatever areas the house has. A chosen Unknown prints as such (lawful).
    { key: t('report.waterSupply'), value: p.waterSupplySource ? descriptorChoiceLabel(t, 'waterSupplySource', p.waterSupplySource) : notRecorded },
    { key: t('report.wasteDisposal'), value: p.wasteDisposalSource ? descriptorChoiceLabel(t, 'wasteDisposalSource', p.wasteDisposalSource) : notRecorded },
    { key: t('report.heatingFuel'), value: p.heatingFuel ? descriptorChoiceLabel(t, 'heatingFuel', p.heatingFuel) : notRecorded }
  ];
  const client: ReportKeyValue[] = [
    // B5 (2026-10-27): the name the client signed the agreement under when the record has none.
    { key: t('report.client'), value: clientNameOf(inspection) || notRecorded },
    { key: t('report.phone'), value: inspection.client.phone || notRecorded },
    { key: t('report.email'), value: inspection.client.email || notRecorded }
  ];
  const inspectionRows: ReportKeyValue[] = [
    { key: t('report.inspectionDate'), value: formatDateTime(locale, inspection.inspectionAt) },
    // NY F1 Round 2 (RP-01 · §444-g(2)): the licensed status and the licence number print on every
    // report — exactly as recorded on THIS inspection, never invented (blank stays blank; a Demo may
    // be blank; a non-Demo blank is a readiness WARNING, src/domain/reportReadiness.ts).
    {
      key: t('report.inspector'),
      value: [
        inspection.inspector.name || notRecorded,
        inspection.inspector.licensedState === 'NY' ? t('report.licensedInspector') : null,
        inspection.inspector.licenseNumber.trim().length > 0 ? `${t('report.licenseNumber')} ${inspection.inspector.licenseNumber.trim()}` : null
      ]
        .filter((s): s is string => s !== null)
        .join(' · ')
    },
    { key: t('report.weather'), value: inspection.weather || notRecorded }
  ];

  const ag = inspection.agreement;
  const fee = ag.demo ? t('report.feeDemo') : feeRecorded(ag.fee) ? t('report.feeAmount', { amount: ag.fee.amount }) : notRecorded;
  const signed = agreementSigned(ag)
    ? t('report.agreementSigned', { client: ag.client.name || inspection.client.name || notRecorded, inspector: ag.inspector.name || inspection.inspector.name || notRecorded, date: formatDate(locale, ag.signedAt ?? deps.generatedAt) })
    : t('report.agreementNotSigned');

  let photoFigure = 0;
  const defectSummary: ReportSummaryLine[] = [];
  const sections: ReportSection[] = [];
  const unable: ReportModel['unable'] = [];
  let limitationCount = 0;
  for (const area of areas) {
    const areaItems = items.filter((i) => i.areaId === area.id).sort((a, b) => a.sortOrder - b.sortOrder);
    const findings: ReportFinding[] = [];
    const nextFigure = () => {
      photoFigure += 1;
      return photoFigure;
    };
    // CA-15 / D-CA-1: a row the inspector marked 缺陷 IS the defect. Its entry is derived from the row —
    // the row's title, its report sentence, the responsible professional, the row's photos. Nothing
    // is stored for it, so a result changed away from 缺陷 removes it at once (no stale defect).
    const morePhotos = (omitted: number) => (omitted > 0 ? tp('report.morePhotos', omitted) : '');
    for (const item of areaItems.filter((i) => i.status === 'issueFound')) {
      const severity = rowSeverity(item);
      const printed = await reportPhotosOf(item.evidenceIds, item.evidenceSuggestions, evidenceById, loadPhoto, t, nextFigure);
      findings.push({
        id: `row-${item.id}`,
        title: itemTitleFor(locale, item),
        itemTitle: null,
        observation: sentenceOf(item, area),
        recommendation: '',
        trade: tradeOf(item),
        priority: '',
        severity,
        severityLabel: severityLabel(t, severity, deps.severityLabels?.[locale]?.[severity]),
        photos: printed.photos,
        morePhotosNote: morePhotos(printed.omitted)
      });
    }
    // OD-13: a voluntary observation is not a finding of the inspection. It leaves the findings list
    // here, once, and lands in its own block — so nothing downstream has to remember the rule.
    const otherObservations: ReportFinding[] = [];
    for (const finding of confirmed.filter((f) => f.areaId === area.id)) {
      const { entry, omittedPhotos } = await assembleFinding(finding, evidenceById, loadPhoto, t, nextFigure);
      const assembled = { ...entry, morePhotosNote: morePhotos(omittedPhotos) };
      if (finding.voluntaryObservation) otherObservations.push({ ...assembled, severity: null, severityLabel: '' });
      else {
        const severity = findingSeverity(finding);
        findings.push({ ...assembled, severity, severityLabel: severity ? severityLabel(t, severity, deps.severityLabels?.[locale]?.[severity]) : '' });
      }
    }
    const areaName = areaDisplayName(locale, area);
    // D-17 ①: the summary lines of this area — every defect row, every defect / safety other finding.
    // 2026-10-28 (iPhone run 10-26 #10): the Safety Review is not a place in the house — its lines say where:
    // the whole house, with the section that describes the defect in brackets.
    const summaryAreaName = area.kind === REVIEW_AREA_KIND ? t('report.reviewAreaLocation', { area: areaName }) : areaName;
    for (const f of findings) {
      if (f.severity === null) continue;
      const fromRow = f.id.startsWith('row-');
      const source = fromRow ? null : confirmed.find((c) => c.id === f.id);
      if (!fromRow && (!source || (source.findingClass !== 'defect' && source.findingClass !== 'safety'))) continue;
      defectSummary.push({ findingId: f.id, severity: f.severity, severityLabel: f.severityLabel, title: f.title, areaName: summaryAreaName, trade: f.trade, photo: f.photos.find((p) => p.dataUrl !== null)?.dataUrl ?? null });
    }
    // NY F1 Round 2: every settled row lands in exactly ONE of observed / limitations / notPresent
    // (RP-R02); the descriptors of the observed rows become the section's 描述信息 (RP-06 … RP-13);
    // N/A and not-installed rows never reach the Unable heading (RP-15 / D-17 ②).
    const descriptors: ReportKeyValue[] = [];
    const observed: ObservedRow[] = [];
    const notPresent: NotPresentRow[] = [];
    const limitations: ReportLimitation[] = [];
    // NY F1 Round 3 (OD-7 = C): the frozen NY wording states the reasons why for an `obstructed`
    // limitation that carries no typed note. R3 narrow repair (F-R3-01): the wording comes from the stored
    // record alone — the inspector's explicit template selection, else the generic obstruction wording;
    // the row being a roof row never turns an obstruction into a solar array. The recorded roof
    // observation method fills the solar wording's method placeholder only when it was recorded — never
    // invented.
    const methodRow = areaItems.find((i) => i.templateId === 'roof-method');
    const methodValue = methodRow ? (itemDescriptorAnswer(methodRow, 'roofObservationMethod')?.value ?? null) : null;
    const roofMethod: RoofObservationMethod | null = isRoofObservationMethod(methodValue) ? methodValue : null;
    const frozenReason = (item: V2ChecklistItem, reasonCode: UnableReason, note: string, scope: LimitationScope, extent: string, templateId: string | null): string => {
      if (note.trim().length > 0) return '';
      const id = resolveLimitationTemplate(reasonCode, templateId);
      return id ? renderLimitationTemplate(id, { locale, component: itemTitleFor(locale, item), extent, scope, method: roofMethod }) : '';
    };
    for (const item of areaItems) {
      const itemTitle = itemTitleFor(locale, item);
      if (item.status === 'notApplicable' || (item.status === 'unableToInspect' && item.reasonCode === 'notInstalled')) {
        notPresent.push({ itemTitle, wording: t('report.notPresentWording'), note: item.statusReason });
        continue;
      }
      if (item.status === 'unableToInspect') {
        limitations.push({
          itemTitle,
          reason: item.reasonCode ? unableReasonLabel(t, item.reasonCode) : notRecorded,
          note: item.statusReason,
          partial: false,
          portion: '',
          reasonText: item.reasonCode ? frozenReason(item, item.reasonCode, item.statusReason, 'whole', '', null) : ''
        });
        continue;
      }
      // NY F1 Round 3 (OD-5 = A / RP-16): a row that keeps its observed result may carry limited
      // portions — each prints as its own limitation entry naming the portion and the reasons why; the
      // row itself stays in Observed Components, marked as observed in part. Never NOT PRESENT, never a
      // whole-row Unable, never an erased result.
      const portions: ReportLimitation[] = item.limitedPortions.map((portion) => ({
        itemTitle,
        reason: unableReasonLabel(t, portion.reasonCode),
        note: portion.note,
        partial: true,
        portion: portion.extent,
        reasonText: frozenReason(item, portion.reasonCode, portion.note, 'partial', portion.extent, portion.templateId)
      }));
      const partial = portions.length > 0;
      const partialLabel = partial ? t('report.observedPartial') : '';
      const partialSuffix = partial ? ` · ${partialLabel}` : '';
      if (item.status === 'notChecked') {
        observed.push({ itemTitle, state: 'notRecorded', label: notRecorded + partialSuffix, stateLabel: notRecorded, partialLabel, partial, sentence: '', supplemental: templateRowScope(item.templateId) === 'supplemental' });
        limitations.push(...portions);
        continue;
      }
      const stateLabel = t(item.status === 'issueFound' ? 'report.observedDefect' : 'report.observed');
      observed.push({ itemTitle, supplemental: templateRowScope(item.templateId) === 'supplemental', sentence: sentenceOf(item, area), state: item.status === 'issueFound' ? 'observedDefect' : 'observed', label: stateLabel + partialSuffix, stateLabel, partialLabel, partial });
      limitations.push(...portions);
      for (const definition of descriptorsForTemplate(item.templateId)) {
        descriptors.push({ key: descriptorLabel(t, definition.key), value: descriptorValueLabel(t, definition, itemDescriptorAnswer(item, definition.key)) ?? notRecorded });
      }
    }
    // The summary counts ITEMS unable to inspect (whole rows, the R2 meaning); limited portions are
    // listed under the limitation headings but are not items.
    limitationCount += limitations.filter((l) => !l.partial).length;
    for (const l of limitations) unable.push({ areaName: areaDisplayName(locale, area), ...l });
    sections.push({
      areaId: area.id,
      title: areaDisplayName(locale, area),
      subtitle: [area.floor !== null ? floorLabelFor(t, area.floor) : null, areaKindLabel(t, area.kind)].filter((s): s is string => s !== null).join(' · '),
      note: area.note,
      results: resultsLine(tp, progressFor(areaItems)),
      descriptors,
      observed,
      notPresent,
      findings,
      otherObservations,
      limitations
    });
  }

  const enabledIds = new Set(areas.map((a: V2Area) => a.id));
  const progress = progressFor(items.filter((i) => enabledIds.has(i.areaId)));
  // OD-9: the service decides the title, the scope statement and the terms this report prints. The
  // text version is the one the client actually signed, so a report delivered after an update still
  // states the scope that was agreed.
  const service = inspectionServiceOf(inspection.property);
  const newBuild = service === 'newBuild';
  const agreementVersion = agreementTextVersion(ag);
  const newBuildDeclaration = locale === 'zh-CN' ? AGREEMENT_NEW_BUILD_DECLARATION_ZH : AGREEMENT_NEW_BUILD_DECLARATION_EN;
  return {
    locale,
    variant,
    service,
    demo: ag.demo,
    title: newBuild ? (locale === 'zh-CN' ? AGREEMENT_NEW_BUILD_REPORT_TITLE_ZH : AGREEMENT_NEW_BUILD_REPORT_TITLE_EN) : t('report.title'),
    variantLabel: variant === 'selfChinese' ? t('report.selfCopy') : variant === 'clientChineseSummary' ? t('report.clientSummaryCopy') : null,
    address: address.length > 0 ? address : notRecorded,
    property,
    client,
    inspection: inspectionRows,
    agreement: {
      document: `${agreementTitleFor(locale, ag.signedService ?? service)} · ${agreementVersionLabel(agreementVersion)}`,
      fee,
      signed
    },
    summary: {
      areas: tp('report.areasInspected', areas.length),
      items: tp('report.itemsInspected', progress.total, { done: progress.settled }),
      // Voluntary observations are courtesy entries, not findings of the inspection (OD-13).
      findings: tp('report.findingsCount', items.filter((i) => enabledIds.has(i.areaId) && i.status === 'issueFound').length + confirmed.filter((f) => enabledIds.has(f.areaId) && !f.voluntaryObservation).length),
      limitations: tp('report.limitationsCount', limitationCount)
    },
    // Red, then orange, then blue; within a tier, the order of the report (area by area).
    defectSummary: DEFECT_SEVERITY_ORDER.flatMap((tier) => defectSummary.filter((line) => line.severity === tier)),
    // Owner item 23: the client's Chinese summary says it is for reference only; English governs.
    governedNote: variant === 'selfChinese' ? t('report.chineseGoverned') : variant === 'clientChineseSummary' ? t('report.clientSummaryGoverned') : null,
    branding: brandingOf(deps.branding, {
      title: newBuild ? (locale === 'zh-CN' ? AGREEMENT_NEW_BUILD_REPORT_TITLE_ZH : AGREEMENT_NEW_BUILD_REPORT_TITLE_EN) : t('report.title'),
      address: address.length > 0 ? address : notRecorded,
      inspector: [inspection.inspector.name, inspection.inspector.licenseNumber.trim().length > 0 ? `${t('report.licenseNumber')} ${inspection.inspector.licenseNumber.trim()}` : ''].filter((s) => s.length > 0).join(' · ')
    }),
    sections,
    systems: systemSummaries(locale, areas, items.filter((i) => enabledIds.has(i.areaId))),
    unable,
    coverPhoto: inspection.coverPhoto ? await coverFor(deps.loadCover ?? mediaCoverLoader, inspectionId) : null,
    inspectorNotes: inspection.notes,
    notObservedLabel: t('report.notObserved'),
    supplementalLabel: t('report.supplementalRow'),
    // §4.2: the New Build declaration replaces the ordinary limited-scope statement (RP-04).
    limitedScopeStatement: newBuild ? newBuildDeclaration : t('report.limitedScopeStatement'),
    // …and the terms section then prints the scope that follows it, so the declaration is stated once.
    terms: newBuild ? agreementScopeBodyWithServicesFor(locale, agreementVersion, ag.additionalServices) : scopeTextFor(locale, { version: agreementVersion, additionalServices: ag.additionalServices }),
    generatedAt: formatDateTime(locale, deps.generatedAt),
    labels: {
      propertyInformation: t('report.propertyInformation'),
      clientInformation: t('report.clientInformation'),
      inspectionDetails: t('report.inspectionDetails'),
      agreement: t('report.agreement'),
      agreementDocument: t('report.agreementDocument'),
      fee: t('report.fee'),
      agreementSigned: t('report.agreementSignedLabel'),
      agreementSeparate: t('report.agreementSeparate'),
      summary: t('report.summary'),
      sections: t('report.sections'),
      systemsSummary: t('report.systemsSummary'),
      description: t('report.description'),
      observedComponents: t('report.observedComponents'),
      notPresent: t('report.notPresentSection'),
      unable: t('report.unableSection'),
      inspectorNotes: t('report.inspectorNotes'),
      terms: t('report.terms'),
      scopeStatement: t('report.scopeStatement'),
      observation: t('report.observation'),
      recommendation: t('report.recommendation'),
      responsibleTrade: t('report.responsibleTrade'),
      priority: t('report.priority'),
      severity: t('report.severity'),
      defectSummary: t('report.defectSummary'),
      // the client's Chinese summary points to the English report for the full text (Owner item 23)
      defectSummaryNote: t(variant === 'clientChineseSummary' ? 'report.clientSummaryDefectNote' : 'report.defectSummaryNote'),
      defectSummaryEmpty: t('report.defectSummaryEmpty'),
      unableSeeArea: t(variant === 'clientChineseSummary' ? 'report.clientSummaryUnableNote' : 'report.unableSeeArea'),
      evidence: t('report.evidence'),
      areaNote: t('report.areaNote'),
      noFindings: t('report.noFindings'),
      reason: t('report.reason'),
      note: t('report.note'),
      portion: t('report.portion'),
      portionUnspecified: t('report.portionUnspecified'),
      otherObservations: t('report.otherObservations'),
      voluntaryTag: t('finding.voluntaryTag'),
      generatedAt: t('report.generatedAt'),
      page: t('report.page'),
      demoBanner: t('report.demoBanner'),
      demoWatermark: t('report.demoWatermark')
    }
  };
}

// ---------- NY F1 Round 4 (RP-02): the Final Review wording reminder ----------
// The enumeration of what the report prints from the inspector's own text lives in ./boundText (the
// closeout reads it too). It is a REMINDER, never a block: generation is not gated on it
// (OWNER-CONTRACT §8.9).
export { reportBoundInspectorText, type WordingScanDeps };

export function wordingRemindersOf(inspectionId: string, deps: WordingScanDeps): WordingReminder[] {
  return wordingRemindersFor(reportBoundInspectorText(inspectionId, deps));
}

// The running header of every page names the company (or, without one, the report title) and the
// property; the footer names the licensed inspector (§444-g(2): the licence on every report). Present
// even without branding, so every page carries the licence and the page number.
function brandingOf(input: ReportBrandingInput | undefined, facts: { title: string; address: string; inspector: string }): ReportModel['branding'] {
  const company = input?.companyName.trim() ?? '';
  return {
    companyName: company,
    contact: input?.contact ?? '',
    logo: input?.logo ?? null,
    inspectorPhoto: input?.inspectorPhoto ?? null,
    runningHeader: [company.length > 0 ? company : facts.title, facts.address].join(' · '),
    runningFooter: facts.inspector
  };
}

// The photos an entry prints (at most REPORT_PHOTOS_PER_ENTRY: the inspector's own links first, then the AI's
// placements) and how many of its photos are left out. Only the printed ones are read and numbered.
async function reportPhotosOf(
  evidenceIds: readonly string[],
  aiPlaced: readonly string[],
  evidenceById: ReadonlyMap<string, { caption: string; annotations?: PhotoAnnotation[] }>,
  loadPhoto: PhotoLoader,
  t: (key: MessageKey, params?: Record<string, string | number>) => string,
  nextFigure: () => number
): Promise<{ photos: ReportPhoto[]; omitted: number }> {
  const known = evidenceIds.filter((id) => evidenceById.has(id));
  const ordered = [...known.filter((id) => !aiPlaced.includes(id)), ...known.filter((id) => aiPlaced.includes(id))];
  const photos: ReportPhoto[] = [];
  for (const evidenceId of ordered.slice(0, REPORT_PHOTOS_PER_ENTRY)) {
    const record = evidenceById.get(evidenceId)!;
    const n = nextFigure();
    // 2026-10-19 (Owner item 22): the inspector's marks are printed on the photo.
    photos.push({ evidenceId, caption: record.caption, dataUrl: await markedPhotoDataUrl(await loadPhoto(evidenceId), record.annotations), figure: t('report.figure', { n }) });
  }
  return { photos, omitted: ordered.length - photos.length };
}

// An OTHER finding (area-level, recorded outside the checklist) — the inspector's own record.
async function assembleFinding(
  finding: V2Finding,
  evidenceById: ReadonlyMap<string, { caption: string; annotations?: PhotoAnnotation[] }>,
  loadPhoto: PhotoLoader,
  t: (key: MessageKey, params?: Record<string, string | number>) => string,
  nextFigure: () => number
): Promise<{ entry: ReportFinding; omittedPhotos: number }> {
  const trade = finding.responsibleTrade ? (finding.responsibleTrade === 'other' ? finding.responsibleTradeOther || tradeLabel(t, 'other') : tradeLabel(t, finding.responsibleTrade)) : null;
  const printed = await reportPhotosOf(finding.evidenceIds, [], evidenceById, loadPhoto, t, nextFigure);
  const entry: ReportFinding = {
    id: finding.id,
    title: finding.title,
    itemTitle: null,
    observation: finding.observation,
    recommendation: finding.recommendation,
    trade,
    priority: priorityLabel(t, finding.priority),
    // set by the caller (it knows whether this is a voluntary observation and the inspector's tier names)
    severity: null,
    severityLabel: '',
    photos: printed.photos,
    morePhotosNote: ''
  };
  return { entry, omittedPhotos: printed.omitted };
}
